/**
 * AK-389 — деталка осмотра (read-only) в правом Sheet.
 *
 * Блоки: находки (степень) · фото-сетка с лайтбоксом · рекомендации · заметка ·
 * мини-карта точки осмотра. Пустые блоки СКРЫВАЮТСЯ (не «—»). Никакого
 * редактирования/удаления в v1 (решение founder).
 */
import { useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { InspectionMiniMap } from './InspectionMiniMap';
import {
  findingsSummary,
  formatInspectionDateTime,
  hasFindingsBlock,
  inspectorLabel,
  noteOf,
  phaseLabel,
  photosWithUrl,
  problemIcon,
  recommendationsOf,
  severityFull,
  sourceChip,
  structuredProblems,
  type Inspection,
} from './inspectionLogic';

interface Props {
  inspection: Inspection | null;
  /** GeoJSON контура поля для мини-карты (Polygon/MultiPolygon). */
  fieldGeojson?: { type?: string; coordinates?: unknown } | null;
  onClose: () => void;
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mt-6 border-t border-border pt-3 first:mt-0 first:border-0 first:pt-0">
      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </div>
      {children}
    </div>
  );
}

export function InspectionDetailSheet({ inspection, fieldGeojson, onClose }: Props) {
  const [lightbox, setLightbox] = useState<string | null>(null);

  const insp = inspection;
  const chip = insp ? sourceChip(insp) : null;
  const photos = insp ? photosWithUrl(insp) : [];
  const problems = insp ? structuredProblems(insp) : [];
  const recs = insp ? recommendationsOf(insp) : [];
  const note = insp ? noteOf(insp) : null;
  const phase = insp ? phaseLabel(insp) : null;
  const summary = insp ? findingsSummary(insp) : null;

  return (
    <>
      <Sheet
        open={!!insp}
        onOpenChange={(open) => {
          if (!open) {
            setLightbox(null);
            onClose();
          }
        }}
      >
        <SheetContent
          side="right"
          className="w-full overflow-y-auto sm:max-w-md"
        >
          {insp && (
            <>
              <SheetHeader>
                <SheetTitle className="flex flex-wrap items-center gap-2 pr-6 text-left">
                  <span>Осмотр · {formatInspectionDateTime(insp.inspected_at)}</span>
                  {chip && <Badge variant={chip.variant}>{chip.label}</Badge>}
                </SheetTitle>
                <SheetDescription className="sr-only">
                  Детали осмотра поля: находки, фото, заметка и точка осмотра.
                </SheetDescription>
              </SheetHeader>

              <div className="mt-4 space-y-0 text-sm">
                {/* Мета */}
                <Section title="Осмотр">
                  <dl className="space-y-1 text-sm text-foreground">
                    <div className="flex gap-2">
                      <dt className="text-muted-foreground">Автор:</dt>
                      <dd className="font-medium">{inspectorLabel(insp)}</dd>
                    </div>
                    {phase && (
                      <div className="flex gap-2">
                        <dt className="text-muted-foreground">Фенофаза:</dt>
                        <dd className="font-medium">{phase}</dd>
                      </div>
                    )}
                  </dl>
                </Section>

                {/* Находки */}
                {insp && hasFindingsBlock(insp) && (
                  <Section title="Находки">
                    {problems.length ? (
                      <ul className="space-y-2">
                        {problems.map((p, i) => {
                          const sev = severityFull(p.severity);
                          return (
                            <li key={i} className="flex items-baseline gap-2">
                              <span aria-hidden>{problemIcon(p.type)}</span>
                              <span className="font-medium">{p.name}</span>
                              {sev && (
                                <span className="text-muted-foreground">— {sev}</span>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    ) : (
                      <Badge variant="primary">Проблем не обнаружено</Badge>
                    )}
                  </Section>
                )}

                {/* Фото */}
                {photos.length > 0 && (
                  <Section title={`Фото (${photos.length})`}>
                    <div className="grid grid-cols-3 gap-2">
                      {photos.map((p, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setLightbox(p.url!)}
                          className="aspect-square overflow-hidden rounded-md border border-border bg-muted focus:outline-none focus:ring-2 focus:ring-ring"
                          aria-label={`Открыть фото ${i + 1}`}
                        >
                          <img
                            src={p.url}
                            alt={`Фото осмотра ${i + 1}`}
                            loading="lazy"
                            className="h-full w-full object-cover transition-transform hover:scale-105"
                          />
                        </button>
                      ))}
                    </div>
                  </Section>
                )}

                {/* Рекомендации */}
                {recs.length > 0 && (
                  <Section title="Рекомендации">
                    <ul className="list-disc space-y-1 pl-6">
                      {recs.map((r, i) => (
                        <li key={i}>{r}</li>
                      ))}
                    </ul>
                  </Section>
                )}

                {/* Заметка */}
                {note && (
                  <Section title="Заметка">
                    <p className="italic text-foreground">«{note}»</p>
                  </Section>
                )}

                {/* Точка осмотра — скрыта если нет GPS */}
                {insp.location && (
                  <Section title="Точка осмотра">
                    <InspectionMiniMap
                      point={insp.location}
                      geojson={fieldGeojson}
                      className="h-40 w-full"
                    />
                  </Section>
                )}

                {/* Совсем пустой осмотр (нет структуры) — не оставляем панель голой */}
                {!hasFindingsBlock(insp) &&
                  !photos.length &&
                  !recs.length &&
                  !note &&
                  !insp.location &&
                  (summary?.tone !== 'ok') && (
                    <Section title="Находки">
                      <p className="text-muted-foreground">
                        В этом осмотре нет структурированных данных.
                      </p>
                    </Section>
                  )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Лайтбокс фото в полный размер */}
      <Dialog open={!!lightbox} onOpenChange={(open) => !open && setLightbox(null)}>
        <DialogContent
          className="max-w-3xl border-0 bg-transparent p-0 shadow-none"
          aria-describedby={undefined}
        >
          <DialogTitle className="sr-only">Фото осмотра</DialogTitle>
          {lightbox && (
            <img
              src={lightbox}
              alt="Фото осмотра в полный размер"
              className="max-h-[85vh] w-full rounded-lg object-contain"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export default InspectionDetailSheet;
